// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — MarketIntelPage
// Price comparison + Exact Monthly Savings Calculator.
// Every line that has overpay is annotated with:
//   • monthly savings potential = (yourPrice - marketPrice) × monthlyConsumption
//   • annual savings = monthly × 12
// Aggregate widget at top = total potential savings across all materials.
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Brain, TrendingUp, TrendingDown, DollarSign, Calculator,
  Target, Zap
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { PageHeader } from '@components/ui/PageHeader';
import { Card, Grid, Badge, Button } from '@components/ui/Primitives';
import { StatCard } from '@components/ui/StatCard';
import { EmptyState } from '@components/ui/States';
import { Modal } from '@components/ui/Modal';

export function MarketIntelPage({ data }) {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [calcModal, setCalcModal] = useState(null);

  // Build the comparison set + compute savings per line
  const comparisonData = useMemo(() => {
    if (!data?.marketPrices?.length) return [];
    return data.marketPrices.map(mp => {
      const userIng = data.ingredients?.find(ing => ing.name_ar === mp.material_name_ar);
      const yourPrice = userIng ? parseFloat(userIng.purchase_price) : null;
      const marketPrice = parseFloat(mp.market_price);
      const monthlyConsumption = userIng ? parseFloat(userIng.actual_consumption_monthly || 0) : 0;

      let diff = null, diffPct = null, monthlySavings = 0, annualSavings = 0, status = 'unknown';

      if (yourPrice !== null && marketPrice > 0) {
        diff = yourPrice - marketPrice;
        diffPct = (diff / marketPrice) * 100;

        if (diff > 0) {
          // Overpaying — these are the savings opportunity
          monthlySavings = diff * monthlyConsumption;
          annualSavings = monthlySavings * 12;
          if (diffPct > 10) status = 'overpay_major';
          else if (diffPct > 3) status = 'overpay_minor';
          else status = 'fair';
        } else if (diff < 0) {
          // You're already under market
          status = 'good';
        } else {
          status = 'fair';
        }
      } else {
        status = 'no_data';
      }

      return {
        ...mp,
        userIng,
        yourPrice,
        diff,
        diffPct,
        monthlyConsumption,
        monthlySavings,
        annualSavings,
        status,
        hasUserPrice: yourPrice !== null
      };
    });
  }, [data]);

  if (comparisonData.length === 0) {
    return (
      <div>
        <PageHeader
          icon={<Brain size={22} color={theme.primary} />}
          title={t.marketIntelTitle}
          description={t.marketIntelDesc}
        />
        <EmptyState
          icon={<Brain size={36} color={theme.primary} />}
          title={t.noDataYet}
          description={t.noDataDesc}
        />
      </div>
    );
  }

  const selected = comparisonData[selectedIdx];
  const trendData = selected?.trend
    ? (typeof selected.trend === 'string' ? JSON.parse(selected.trend) : selected.trend)
    : [];

  // ─── Aggregate savings ──────────────────────────────────────────────
  const overpayingLines = comparisonData.filter(d => d.status === 'overpay_major' || d.status === 'overpay_minor');
  const totalMonthlySavings = overpayingLines.reduce((sum, d) => sum + d.monthlySavings, 0);
  const totalAnnualSavings = totalMonthlySavings * 12;
  const topOverpay = [...overpayingLines].sort((a, b) => b.monthlySavings - a.monthlySavings)[0];

  const savingsCount = comparisonData.filter(d => d.status === 'good').length;
  const tooltipStyle = {
    background: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    fontSize: 12,
    padding: '8px 12px'
  };

  const getStatusBadge = (status) => {
    if (status === 'overpay_major') return <Badge variant="danger">{t.statusOverpay}</Badge>;
    if (status === 'overpay_minor') return <Badge variant="warning">{t.statusOverpay}</Badge>;
    if (status === 'good') return <Badge variant="success">{t.statusGood}</Badge>;
    if (status === 'fair') return <Badge variant="info">{lang === 'ar' ? 'مقبول' : 'Fair'}</Badge>;
    return <Badge variant="neutral">—</Badge>;
  };

  return (
    <div>
      <PageHeader
        icon={<Brain size={22} color={theme.primary} />}
        title={t.marketIntelTitle}
        description={t.marketIntelDesc}
      />

      {/* ═══ Hero Savings Widget ═══ */}
      {totalMonthlySavings > 0 && (
        <Card style={{
          padding: '24px 26px',
          marginBottom: 24,
          background: `linear-gradient(135deg, ${theme.primaryDim}, ${theme.infoDim})`,
          border: `1px solid ${theme.primaryBorder}`
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 6
              }}>
                <Target size={16} color={theme.primary} />
                <span style={{ fontSize: 12, fontWeight: 700, color: theme.primary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t.totalPotentialSavings}
                </span>
              </div>
              <div style={{ fontSize: 34, fontWeight: 800, color: theme.text, letterSpacing: -1 }}>
                {t.sar} {totalMonthlySavings.toFixed(0).toLocaleString()}
                <span style={{ fontSize: 14, fontWeight: 500, color: theme.textSecondary, marginLeft: 8 }}>
                  / {lang === 'ar' ? 'شهرياً' : 'month'}
                </span>
              </div>
              <div style={{ fontSize: 13, color: theme.textSecondary, marginTop: 6 }}>
                {t.annualSavings}: <strong style={{ color: theme.primary }}>{t.sar} {totalAnnualSavings.toLocaleString()}</strong>
                {' · '}
                {lang === 'ar' ? `عبر ${overpayingLines.length} مادة` : `across ${overpayingLines.length} materials`}
              </div>
            </div>
            {topOverpay && (
              <div style={{
                background: theme.bgCard,
                borderRadius: 10,
                padding: '14px 18px',
                border: `1px solid ${theme.border}`,
                minWidth: 200
              }}>
                <div style={{ fontSize: 10, color: theme.textTertiary, marginBottom: 4 }}>
                  {lang === 'ar' ? 'الأكبر توفيراً' : 'Biggest saver'}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, marginBottom: 4 }}>
                  {lang === 'ar' ? topOverpay.material_name_ar : topOverpay.material_name_en}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: theme.success }}>
                  {t.sar} {topOverpay.monthlySavings.toFixed(0)}/{lang === 'ar' ? 'شهر' : 'mo'}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* KPI row */}
      <div style={{ marginBottom: 24 }}>
        <Grid min={220}>
          <StatCard
            icon={<DollarSign size={20} color={theme.primary} />}
            label={lang === 'ar' ? 'مواد مراقبة' : 'Materials Tracked'}
            value={comparisonData.length}
          />
          <StatCard
            icon={<TrendingUp size={20} color={theme.danger} />}
            label={lang === 'ar' ? 'تدفع أكثر' : 'Overpaying'}
            value={overpayingLines.length}
            subtext={`${t.sar} ${totalMonthlySavings.toFixed(0)} ${lang === 'ar' ? 'شهرياً' : '/mo'}`}
            accentColor={theme.danger}
          />
          <StatCard
            icon={<TrendingDown size={20} color={theme.success} />}
            label={lang === 'ar' ? 'سعر جيد' : 'Good Pricing'}
            value={savingsCount}
            subtext={lang === 'ar' ? 'مواد تحت السوق' : 'below market'}
            accentColor={theme.success}
          />
        </Grid>
      </div>

      {/* Comparison table */}
      <Card style={{ overflow: 'hidden', marginBottom: 24 }}>
        <div style={{
          padding: '16px 22px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <Calculator size={16} color={theme.primary} />
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.text }}>
            {lang === 'ar' ? 'مقارنة الأسعار + حساب الوفر' : 'Price Comparison + Savings Calc'}
          </h4>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                {[
                  t.material,
                  t.marketPrice,
                  t.yourPrice,
                  t.priceDifference,
                  t.potentialMonthlySavings,
                  t.status,
                  ''
                ].map((h, i) => (
                  <th key={i} style={{
                    padding: '10px 14px',
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: 11,
                    fontWeight: 600,
                    color: theme.textTertiary,
                    letterSpacing: 0.5
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, idx) => {
                const isSelected = selectedIdx === idx;
                const isOverpay = row.status === 'overpay_major' || row.status === 'overpay_minor';
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${theme.borderLight}`,
                      cursor: 'pointer',
                      background: isSelected ? theme.primaryDim : 'transparent',
                      transition: 'background 0.15s'
                    }}
                    onClick={() => setSelectedIdx(idx)}
                  >
                    <td style={{
                      padding: '12px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      color: theme.text
                    }}>
                      {lang === 'ar' ? row.material_name_ar : row.material_name_en}
                      <span style={{ fontSize: 11, color: theme.textTertiary, marginLeft: 6 }}>
                        /{row.unit}
                      </span>
                    </td>
                    <td style={{
                      padding: '12px 14px',
                      fontSize: 13,
                      color: theme.textSecondary
                    }}>
                      {t.sar} {parseFloat(row.market_price).toFixed(2)}
                    </td>
                    <td style={{
                      padding: '12px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      color: row.hasUserPrice
                        ? (isOverpay ? theme.danger : row.status === 'good' ? theme.success : theme.text)
                        : theme.textTertiary
                    }}>
                      {row.hasUserPrice ? `${t.sar} ${row.yourPrice.toFixed(2)}` : '—'}
                    </td>
                    <td style={{
                      padding: '12px 14px',
                      fontSize: 13,
                      fontWeight: 600,
                      color: row.hasUserPrice ? (isOverpay ? theme.danger : theme.success) : theme.textTertiary
                    }}>
                      {row.hasUserPrice
                        ? `${row.diff >= 0 ? '+' : ''}${row.diff.toFixed(2)} (${row.diffPct >= 0 ? '+' : ''}${row.diffPct.toFixed(1)}%)`
                        : '—'
                      }
                    </td>
                    <td style={{
                      padding: '12px 14px',
                      fontSize: 13,
                      fontWeight: 700,
                      color: row.monthlySavings > 0 ? theme.success : theme.textTertiary
                    }}>
                      {row.monthlySavings > 0
                        ? `${t.sar} ${row.monthlySavings.toFixed(0)}/${lang === 'ar' ? 'شهر' : 'mo'}`
                        : '—'
                      }
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {getStatusBadge(row.status)}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {row.monthlySavings > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); setCalcModal(row); }}
                        >
                          <Calculator size={13} />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Price trend chart */}
      {selected && trendData.length > 0 && (
        <Card style={{ padding: 22 }}>
          <h4 style={{
            margin: '0 0 16px',
            fontSize: 14,
            fontWeight: 700,
            color: theme.text
          }}>
            {t.priceTrend} — {lang === 'ar' ? selected.material_name_ar : selected.material_name_en}
          </h4>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
              <XAxis
                dataKey="date"
                tick={{ fill: theme.textTertiary, fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: theme.textTertiary, fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                domain={['auto', 'auto']}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value) => `${t.sar} ${value}`}
              />
              <Line
                type="monotone"
                dataKey="price"
                stroke={theme.chartColors[0]}
                strokeWidth={2.5}
                dot={{ r: 4, fill: theme.chartColors[0] }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ═══ Savings Calculation Modal ═══ */}
      <Modal
        open={!!calcModal}
        onClose={() => setCalcModal(null)}
        title={t.savingsCalcDetail}
      >
        {calcModal && (
          <div>
            <div style={{
              padding: '14px 16px',
              background: theme.bgInput,
              borderRadius: 10,
              marginBottom: 16
            }}>
              <div style={{ fontSize: 11, color: theme.textTertiary, marginBottom: 2 }}>
                {t.material}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>
                {lang === 'ar' ? calcModal.material_name_ar : calcModal.material_name_en}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <CalcRow
                label={t.yourPrice}
                value={`${t.sar} ${calcModal.yourPrice.toFixed(2)}`}
                sublabel={`/ ${calcModal.unit}`}
                theme={theme}
                color={theme.danger}
              />
              <CalcRow
                label={t.marketPrice}
                value={`${t.sar} ${parseFloat(calcModal.market_price).toFixed(2)}`}
                sublabel={`/ ${calcModal.unit}`}
                theme={theme}
                color={theme.success}
              />
              <CalcRow
                label={t.priceDifference}
                value={`${t.sar} ${calcModal.diff.toFixed(2)}`}
                sublabel={`(${calcModal.diffPct.toFixed(1)}%)`}
                theme={theme}
                color={theme.danger}
                bold
              />
            </div>

            <div style={{
              padding: '12px 14px',
              background: theme.bgInput,
              borderRadius: 8,
              marginBottom: 16,
              fontSize: 13,
              color: theme.textSecondary,
              lineHeight: 1.7
            }}>
              <Calculator size={13} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} color={theme.primary} />
              <strong>{lang === 'ar' ? 'المعادلة' : 'Formula'}:</strong>{' '}
              {t.sar} {calcModal.diff.toFixed(2)}
              {' × '}
              {calcModal.monthlyConsumption.toFixed(1)} {calcModal.unit}
              {' = '}
              <strong style={{ color: theme.success }}>
                {t.sar} {calcModal.monthlySavings.toFixed(0)}/{lang === 'ar' ? 'شهر' : 'mo'}
              </strong>
            </div>

            <div style={{
              padding: '18px 20px',
              background: `linear-gradient(135deg, ${theme.successDim}, ${theme.primaryDim})`,
              borderRadius: 12,
              border: `1px solid ${theme.primaryBorder}`,
              textAlign: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                marginBottom: 4
              }}>
                <Zap size={14} color={theme.primary} />
                <span style={{ fontSize: 11, color: theme.primary, fontWeight: 700, textTransform: 'uppercase' }}>
                  {t.potentialMonthlySavings}
                </span>
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: theme.success, letterSpacing: -0.5 }}>
                {t.sar} {calcModal.monthlySavings.toFixed(0)}
              </div>
              <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4 }}>
                {t.annualSavings}: <strong>{t.sar} {calcModal.annualSavings.toFixed(0).toLocaleString()}</strong>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function CalcRow({ label, value, sublabel, theme, color, bold }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: `1px solid ${theme.borderLight}`
    }}>
      <span style={{ fontSize: 12, color: theme.textSecondary }}>{label}</span>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontSize: 14, fontWeight: bold ? 700 : 600, color: color || theme.text }}>
          {value}
        </span>
        {sublabel && (
          <span style={{ fontSize: 11, color: theme.textTertiary, marginLeft: 6 }}>
            {sublabel}
          </span>
        )}
      </div>
    </div>
  );
}
